import { useEffect, useState } from 'react'
import { Search } from 'lucide-react'
import { api } from '../../api/client'

// Shape returned by GET /api/admin/users/{id}/briefing-trace.
// Mirrors the backend dict so we can render without further parsing.
interface OutputMatch {
  id: string
  action_type: string
  title: string
  excerpt: string
  dismissed: boolean
  delivered_at: string | null
  source_memory_ids: string[]
  created_at: string
  age_hours: number | null
}

interface MemoryMatch {
  id: string
  source_type: string
  source_ref: string | null
  tags: string[]
  epistemic_status: string
  deadline_at: string | null
  importance: number
  area_id: string | null
  excerpt: string
  created_at: string
  age_hours: number | null
}

interface VaultMatch {
  vault_file_path: string
  filename: string
  heading_path: string | null
  chunk_index: number
  excerpt: string
  file_updated_at: string | null
}

interface EntityMatch {
  id: string
  type: string
  title: string
  canonical_key: string | null
  aliases: string[]
  state: string | null
  updated_at: string | null
}

interface TraceResponse {
  user_id: string
  contains: string
  hours_back: number
  since: string
  now: string
  matches: {
    output_actions: OutputMatch[]
    memories: MemoryMatch[]
    vault: VaultMatch[]
    entities: EntityMatch[]
  }
  summary: {
    output_actions: number
    memories: number
    vault: number
    entities: number
    total: number
  }
}

interface AdminMe {
  id: string
  email: string
}

// ── Styled primitives — mirror existing admin pages (UsersPage et al.) ─

const cardStyle: React.CSSProperties = {
  background: 'var(--bg-elev)',
  border: '1px solid var(--border-subtle)',
  borderRadius: '12px',
  padding: '20px',
}

const labelStyle: React.CSSProperties = {
  fontFamily: 'var(--font-mono)',
  fontSize: '11px',
  color: 'var(--ink-muted)',
  textTransform: 'uppercase',
  letterSpacing: '0.12em',
  marginBottom: '8px',
}

const inputStyle: React.CSSProperties = {
  width: '100%',
  padding: '10px 12px',
  fontFamily: 'var(--font-ui)',
  fontSize: '14px',
  background: 'var(--bg)',
  border: '1px solid var(--border)',
  borderRadius: '8px',
  color: 'var(--ink)',
  outline: 'none',
}

const tagStyle: React.CSSProperties = {
  fontFamily: 'var(--font-mono)',
  fontSize: '10px',
  textTransform: 'uppercase',
  letterSpacing: '0.08em',
  padding: '2px 8px',
  borderRadius: '4px',
  background: 'var(--bg-elev-2, rgba(0,0,0,0.04))',
  color: 'var(--ink-muted)',
}

function ageLabel(hours: number | null): string {
  if (hours == null) return '—'
  if (hours < 1) return `${Math.round(hours * 60)} min`
  if (hours < 48) return `${Math.round(hours)} h`
  return `${Math.round(hours / 24)} d`
}

export function TracePage() {
  const [me, setMe] = useState<AdminMe | null>(null)
  const [userId, setUserId] = useState('')
  const [contains, setContains] = useState('')
  const [hours, setHours] = useState(72)
  const [data, setData] = useState<TraceResponse | null>(null)
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)

  // Auto-populate user_id with the logged-in admin so the common path
  // ("trace my own briefings") is one click away. The user can replace
  // it to investigate another user's briefings.
  useEffect(() => {
    api.get<AdminMe>('/admin/me').then((m) => {
      setMe(m)
      setUserId((prev) => prev || m.id)
    }).catch(() => {
      // /me failure isn't fatal — user can paste their UUID manually.
    })
  }, [])

  async function search() {
    if (!userId || !contains || contains.length < 2) {
      setError('Preenche User ID e substring (mínimo 2 caracteres).')
      return
    }
    setLoading(true)
    setError(null)
    try {
      const r = await api.get<TraceResponse>(
        `/admin/users/${userId}/briefing-trace?contains=${encodeURIComponent(contains)}&hours=${hours}`,
      )
      setData(r)
    } catch (e: any) {
      setError(e.message || 'Falha na busca')
      setData(null)
    } finally {
      setLoading(false)
    }
  }

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: '24px' }}>
      <div>
        <h1
          style={{
            fontFamily: 'var(--font-display)',
            fontSize: '28px',
            fontWeight: 600,
            color: 'var(--ink)',
            margin: 0,
            marginBottom: '6px',
          }}
        >
          Briefing Trace
        </h1>
        <p
          style={{
            fontFamily: 'var(--font-ui)',
            fontSize: '13px',
            color: 'var(--ink-muted)',
            margin: 0,
          }}
        >
          Procura onde uma string aparece em briefings, memórias, vault e entidades. Útil para perceber porque algo continua a surgir nos outputs.
        </p>
      </div>

      {/* Search form */}
      <div style={cardStyle}>
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 120px', gap: '12px', marginBottom: '12px' }}>
          <div>
            <div style={labelStyle}>Substring (case-insensitive)</div>
            <input
              type="text"
              placeholder="ex: contabilistas"
              value={contains}
              onChange={(e) => setContains(e.target.value)}
              onKeyDown={(e) => e.key === 'Enter' && search()}
              style={inputStyle}
            />
          </div>
          <div>
            <div style={labelStyle}>User ID</div>
            <input
              type="text"
              placeholder="UUID"
              value={userId}
              onChange={(e) => setUserId(e.target.value)}
              style={{ ...inputStyle, fontFamily: 'var(--font-mono)', fontSize: '12px' }}
            />
          </div>
          <div>
            <div style={labelStyle}>Janela</div>
            <select
              value={hours}
              onChange={(e) => setHours(Number(e.target.value))}
              style={inputStyle}
            >
              <option value={24}>24h</option>
              <option value={72}>72h</option>
              <option value={168}>7 dias</option>
              <option value={720}>30 dias</option>
            </select>
          </div>
        </div>
        <button
          onClick={search}
          disabled={loading || !contains || !userId}
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: '8px',
            padding: '10px 18px',
            background: loading ? 'var(--ink-muted)' : 'var(--ink-1)',
            color: 'var(--cream, #FAFAF7)',
            border: 'none',
            borderRadius: '8px',
            fontFamily: 'var(--font-ui)',
            fontSize: '13px',
            fontWeight: 500,
            cursor: loading || !contains ? 'not-allowed' : 'pointer',
            opacity: !contains || !userId ? 0.5 : 1,
          }}
        >
          <Search size={14} />
          {loading ? 'A procurar…' : 'Procurar'}
        </button>
        {me && userId !== me.id && (
          <button
            onClick={() => setUserId(me.id)}
            style={{
              marginLeft: '8px',
              padding: '10px 14px',
              background: 'transparent',
              color: 'var(--ink-muted)',
              border: '1px solid var(--border)',
              borderRadius: '8px',
              fontFamily: 'var(--font-ui)',
              fontSize: '12px',
              cursor: 'pointer',
            }}
          >
            usar o meu user_id
          </button>
        )}
        {error && (
          <div
            style={{
              marginTop: '12px',
              padding: '10px 14px',
              background: 'rgba(220,80,80,0.08)',
              border: '1px solid rgba(220,80,80,0.2)',
              borderRadius: '8px',
              color: '#a14040',
              fontFamily: 'var(--font-ui)',
              fontSize: '13px',
            }}
          >
            {error}
          </div>
        )}
      </div>

      {/* Results */}
      {data && (
        <>
          <div
            style={{
              display: 'flex',
              gap: '16px',
              fontFamily: 'var(--font-mono)',
              fontSize: '12px',
              color: 'var(--ink-muted)',
            }}
          >
            <span>{data.summary.total} match{data.summary.total === 1 ? '' : 'es'} total</span>
            <span>·</span>
            <span>{data.summary.output_actions} outputs</span>
            <span>·</span>
            <span>{data.summary.memories} memórias</span>
            <span>·</span>
            <span>{data.summary.vault} vault</span>
            <span>·</span>
            <span>{data.summary.entities} entidades</span>
          </div>

          {data.matches.output_actions.length > 0 && (
            <Section label={`Output actions (${data.matches.output_actions.length})`}>
              {data.matches.output_actions.map((m) => (
                <div key={m.id} style={cardStyle}>
                  <div style={{ display: 'flex', gap: '8px', marginBottom: '8px', alignItems: 'center' }}>
                    <span style={tagStyle}>{m.action_type}</span>
                    {m.dismissed && <span style={{ ...tagStyle, color: '#888' }}>dismissed</span>}
                    <span style={{ ...tagStyle, marginLeft: 'auto' }}>{ageLabel(m.age_hours)}</span>
                  </div>
                  <div style={{ fontFamily: 'var(--font-ui)', fontWeight: 500, fontSize: '14px', marginBottom: '6px' }}>
                    {m.title}
                  </div>
                  <pre
                    style={{
                      whiteSpace: 'pre-wrap',
                      fontFamily: 'var(--font-mono)',
                      fontSize: '12px',
                      color: 'var(--ink-light)',
                      background: 'var(--bg)',
                      padding: '10px',
                      borderRadius: '6px',
                      margin: 0,
                      maxHeight: '200px',
                      overflowY: 'auto',
                    }}
                  >
                    {m.excerpt}
                  </pre>
                  <div style={{ fontFamily: 'var(--font-mono)', fontSize: '10px', color: 'var(--ink-muted)', marginTop: '6px' }}>
                    id: {m.id} · created: {new Date(m.created_at).toLocaleString('pt-PT')}
                  </div>
                </div>
              ))}
            </Section>
          )}

          {data.matches.memories.length > 0 && (
            <Section label={`Memórias (${data.matches.memories.length})`}>
              {data.matches.memories.map((m) => (
                <div key={m.id} style={cardStyle}>
                  <div style={{ display: 'flex', gap: '8px', marginBottom: '8px', alignItems: 'center', flexWrap: 'wrap' }}>
                    <span style={tagStyle}>{m.source_type}</span>
                    <span style={tagStyle}>{m.epistemic_status}</span>
                    {m.tags.map((t) => (
                      <span key={t} style={{ ...tagStyle, color: 'var(--accent, #5a3d2d)' }}>{t}</span>
                    ))}
                    <span style={{ ...tagStyle, marginLeft: 'auto' }}>{ageLabel(m.age_hours)}</span>
                  </div>
                  <pre
                    style={{
                      whiteSpace: 'pre-wrap',
                      fontFamily: 'var(--font-mono)',
                      fontSize: '12px',
                      color: 'var(--ink-light)',
                      background: 'var(--bg)',
                      padding: '10px',
                      borderRadius: '6px',
                      margin: 0,
                      maxHeight: '200px',
                      overflowY: 'auto',
                    }}
                  >
                    {m.excerpt}
                  </pre>
                  <div style={{ fontFamily: 'var(--font-mono)', fontSize: '10px', color: 'var(--ink-muted)', marginTop: '6px' }}>
                    id: {m.id}
                    {m.source_ref && ` · ref: ${m.source_ref}`}
                    {m.deadline_at && ` · deadline: ${new Date(m.deadline_at).toLocaleDateString('pt-PT')}`}
                  </div>
                </div>
              ))}
            </Section>
          )}

          {data.matches.vault.length > 0 && (
            <Section label={`Vault (${data.matches.vault.length})`}>
              {data.matches.vault.map((m, i) => (
                <div key={i} style={cardStyle}>
                  <div style={{ fontFamily: 'var(--font-mono)', fontSize: '12px', color: 'var(--ink)', fontWeight: 600, marginBottom: '6px' }}>
                    {m.vault_file_path}
                    {m.heading_path && <span style={{ color: 'var(--ink-muted)', fontWeight: 400 }}> › {m.heading_path}</span>}
                  </div>
                  <pre
                    style={{
                      whiteSpace: 'pre-wrap',
                      fontFamily: 'var(--font-mono)',
                      fontSize: '12px',
                      color: 'var(--ink-light)',
                      background: 'var(--bg)',
                      padding: '10px',
                      borderRadius: '6px',
                      margin: 0,
                      maxHeight: '200px',
                      overflowY: 'auto',
                    }}
                  >
                    {m.excerpt}
                  </pre>
                  <div style={{ fontFamily: 'var(--font-mono)', fontSize: '10px', color: 'var(--ink-muted)', marginTop: '6px' }}>
                    chunk #{m.chunk_index}
                    {m.file_updated_at && ` · file updated: ${new Date(m.file_updated_at).toLocaleString('pt-PT')}`}
                  </div>
                </div>
              ))}
            </Section>
          )}

          {data.matches.entities.length > 0 && (
            <Section label={`Entidades (${data.matches.entities.length})`}>
              {data.matches.entities.map((e) => (
                <div key={e.id} style={cardStyle}>
                  <div style={{ display: 'flex', gap: '8px', alignItems: 'center', marginBottom: '6px' }}>
                    <span style={tagStyle}>{e.type}</span>
                    {e.state && <span style={tagStyle}>{e.state}</span>}
                  </div>
                  <div style={{ fontFamily: 'var(--font-ui)', fontWeight: 500, fontSize: '14px' }}>
                    {e.title}
                  </div>
                  {e.aliases && e.aliases.length > 0 && (
                    <div style={{ fontFamily: 'var(--font-mono)', fontSize: '11px', color: 'var(--ink-muted)', marginTop: '4px' }}>
                      aliases: {e.aliases.join(', ')}
                    </div>
                  )}
                  <div style={{ fontFamily: 'var(--font-mono)', fontSize: '10px', color: 'var(--ink-muted)', marginTop: '6px' }}>
                    id: {e.id}
                    {e.canonical_key && ` · key: ${e.canonical_key}`}
                  </div>
                </div>
              ))}
            </Section>
          )}

          {data.summary.total === 0 && (
            <div
              style={{
                ...cardStyle,
                textAlign: 'center',
                color: 'var(--ink-muted)',
                fontFamily: 'var(--font-ui)',
                fontSize: '14px',
              }}
            >
              Nenhum match encontrado para "{data.contains}" nas últimas {data.hours_back}h.
              <div style={{ fontSize: '12px', marginTop: '6px' }}>
                Se a string aparece num briefing recente mesmo assim, é provável alucinação do LLM (não tem fonte real no Brain).
              </div>
            </div>
          )}
        </>
      )}
    </div>
  )
}

function Section({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
      <div
        style={{
          fontFamily: 'var(--font-mono)',
          fontSize: '11px',
          textTransform: 'uppercase',
          letterSpacing: '0.14em',
          color: 'var(--ink-muted)',
        }}
      >
        {label}
      </div>
      {children}
    </div>
  )
}
